import { useEffect, useRef, useState } from 'react'
import { Check, Loader2, MapPin } from 'lucide-react'
import { Campo, Entrada } from './ui/base'
import { buscarDirecciones, resolverSugerencia, type SugerenciaDireccion } from '../lib/geo'
import { uid } from '../lib/cn'
import { cn } from '../lib/cn'

/** Campo de dirección con autocompletado tipo Google Maps: busca en el
 *  mapa mientras se escribe (con una pausa breve para no consultar en
 *  cada letra) y deja elegir la que corresponde de una lista, en vez
 *  de confiar en que quien escribe no se equivocó. Elegir una marca la
 *  dirección como confirmada y entrega sus coordenadas —las mismas que
 *  después usa Despachos para armar la ruta, así que quedan listas de
 *  una vez en lugar de tener que geocodificarla después. */
export function CampoDireccion({
  valor,
  onCambiar,
  onElegir,
  confirmada,
  requerido,
  error,
}: {
  valor: string
  onCambiar: (texto: string) => void
  onElegir: (lugar: { calle: string; comuna?: string; lat: number; lng: number }) => void
  /** True cuando lo que hay escrito viene de una sugerencia elegida y
   *  no se ha tocado desde entonces. */
  confirmada: boolean
  requerido?: boolean
  error?: string
}) {
  const [sugerencias, setSugerencias] = useState<SugerenciaDireccion[]>([])
  const [abierto, setAbierto] = useState(false)
  const [buscando, setBuscando] = useState(false)
  const [resolviendo, setResolviendo] = useState<string | null>(null)
  const [resaltado, setResaltado] = useState(0)
  const contenedorRef = useRef<HTMLDivElement>(null)
  const controladorRef = useRef<AbortController | null>(null)
  // Google cobra el autocompletado y la ficha del lugar juntos, más
  // barato, si ambos llamados van con el mismo "token de sesión" —una
  // por cada vez que la persona busca una dirección de principio a
  // fin. Se renueva apenas se elige algo, para que la próxima búsqueda
  // sea una sesión aparte.
  const sesionRef = useRef(uid())

  // Busca con una pausa después de la última tecla —así no se manda una
  // consulta por cada letra, solo cuando la persona deja de escribir un
  // momento. Menos de 4 caracteres no alcanza para que el buscador
  // devuelva algo útil.
  useEffect(() => {
    controladorRef.current?.abort()
    if (confirmada || valor.trim().length < 4) {
      setSugerencias([])
      setBuscando(false)
      return
    }
    setBuscando(true)
    const espera = setTimeout(async () => {
      const controlador = new AbortController()
      controladorRef.current = controlador
      const resultados = await buscarDirecciones(valor, sesionRef.current, controlador.signal)
      if (controlador.signal.aborted) return
      setSugerencias(resultados)
      setBuscando(false)
      setResaltado(0)
      setAbierto(true)
    }, 450)
    return () => clearTimeout(espera)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valor, confirmada])

  // Clic afuera cierra la lista.
  useEffect(() => {
    if (!abierto) return
    const cerrar = (e: MouseEvent) => {
      if (contenedorRef.current && !contenedorRef.current.contains(e.target as Node)) {
        setAbierto(false)
      }
    }
    document.addEventListener('mousedown', cerrar)
    return () => document.removeEventListener('mousedown', cerrar)
  }, [abierto])

  const elegir = async (s: SugerenciaDireccion) => {
    setAbierto(false)
    setResolviendo(s.id)
    const lugar = await resolverSugerencia(s, sesionRef.current)
    setResolviendo(null)
    if (!lugar) return
    onElegir(lugar)
    setSugerencias([])
    // Sesión terminada: la próxima búsqueda arranca una nueva.
    sesionRef.current = uid()
  }

  return (
    <Campo etiqueta="Dirección" requerido={requerido} error={error}>
      <div ref={contenedorRef} className="relative">
        <div className="relative">
          <Entrada
            value={valor}
            onChange={(e) => onCambiar(e.target.value)}
            onFocus={() => sugerencias.length > 0 && setAbierto(true)}
            onKeyDown={(e) => {
              if (!abierto || sugerencias.length === 0) return
              if (e.key === 'ArrowDown') {
                e.preventDefault()
                setResaltado((i) => Math.min(i + 1, sugerencias.length - 1))
              } else if (e.key === 'ArrowUp') {
                e.preventDefault()
                setResaltado((i) => Math.max(i - 1, 0))
              } else if (e.key === 'Enter') {
                e.preventDefault()
                void elegir(sugerencias[resaltado])
              } else if (e.key === 'Escape') {
                setAbierto(false)
              }
            }}
            placeholder="Av. Providencia 2134"
            className="pr-10"
            autoComplete="off"
          />
          <span className="pointer-events-none absolute top-1/2 right-3.5 -translate-y-1/2">
            {buscando || resolviendo ? (
              <Loader2 className="size-4 animate-spin texto-tenue" />
            ) : confirmada ? (
              <Check className="size-4 text-lima-500" strokeWidth={3} />
            ) : null}
          </span>
        </div>

        {confirmada && (
          <p className="mt-1.5 flex items-center gap-1 text-[12px] font-medium text-lima-600 dark:text-lima-300">
            <Check className="size-3" strokeWidth={3} />
            Dirección confirmada en el mapa
          </p>
        )}

        {abierto && sugerencias.length > 0 && (
          <ul className="absolute z-20 mt-1.5 w-full overflow-hidden rounded-2xl border borde-base superficie-elevada py-1 sombra-flotante">
            {sugerencias.map((s, i) => (
              <li key={s.id}>
                <button
                  type="button"
                  disabled={resolviendo !== null}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => void elegir(s)}
                  onMouseEnter={() => setResaltado(i)}
                  className={cn(
                    'flex w-full items-start gap-2.5 px-3.5 py-2.5 text-left transition-colors disabled:opacity-50',
                    i === resaltado ? 'superficie-hundida' : 'hover:superficie-hundida',
                  )}
                >
                  {resolviendo === s.id ? (
                    <Loader2 className="mt-0.5 size-4 shrink-0 animate-spin texto-tenue" />
                  ) : (
                    <MapPin className="mt-0.5 size-4 shrink-0 texto-tenue" />
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[14px] font-semibold texto-base">
                      {s.calle}
                    </span>
                    <span className="block truncate text-[12px] texto-suave">{s.detalle}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Campo>
  )
}
